import cn from "classnames";
import Image from "next/image";
import { useMemo } from "react";
import Slider from "@mui/material/Slider";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { DatePicker } from "@/src/component/base/DatePicker";
import { PROPERTY_FILTER_KEYS } from "@/src/const";
import { ProviderHooks, StyleHooks } from "@/src/hook";
import { AvailableHomesLogic } from "@/src/logic";
import { StrapiComponentType } from "@/src/type";
import { StringHelper, MoneyHelper } from "@/src/util";

import styles from "./AvailableHomesFilters.module.scss";

function convertToNumber(value: string | string[] | number): number {
  if (typeof value === "string") {
    try {
      return parseInt(value, 10);
    } catch (e) {}
  }
  return 0;
  ("");
}

interface AvailableHomesFiltersOptionsProps {
  availableHomesFilters: StrapiComponentType.Section.AvailableHomesFilters;
  isDialog: boolean;
  openDialog: () => void;
}

function AvailableHomesFiltersOptions({ availableHomesFilters, isDialog, openDialog }: AvailableHomesFiltersOptionsProps) {
  const FILTER_KEYS = PROPERTY_FILTER_KEYS;
  const HIGH_NUMBER = 9999999999;
  const { floorplans, filters, updateFilter } = ProviderHooks.useProperty();
  const isSmall = StyleHooks.useBreakPointDown("sm");
  const maxBudget = useMemo(
    () => floorplans.reduce((acc, floorplan) => ((floorplan.startingAtPrice ?? 0) > acc ? (floorplan.startingAtPrice ?? 0) : acc), 0),
    [floorplans]
  );
  const minBudget = useMemo(
    () =>
      floorplans.reduce(
        (acc, floorplan) => (Math.max(floorplan.startingAtPrice ?? HIGH_NUMBER) < acc ? Math.max(floorplan.startingAtPrice ?? HIGH_NUMBER) : acc),
        HIGH_NUMBER
      ),
    [floorplans]
  );
  const currencySymbol = MoneyHelper.getCurrencySymbol(floorplans[0]?.startingAtCurrency);

  const minBath = useMemo(
    () => floorplans.reduce((acc, floorplan) => ((floorplan.bathrooms ?? 1) < acc ? (floorplan.bathrooms ?? 1) : acc), 1),
    [floorplans]
  );
  const maxBath = useMemo(
    () => floorplans.reduce((acc, floorplan) => ((floorplan.bathrooms ?? 1) > acc ? (floorplan.bathrooms ?? 1) : acc), 1),
    [floorplans]
  );
  const filterCount = useMemo(() => {
    return Object.keys(filters ?? {}).length ?? 0;
  }, [filters]);

  const handleBedsChange = (event: React.MouseEvent<HTMLElement>, value: string) => {
    updateFilter(FILTER_KEYS.BEDS.param, value);
  };

  const handleBathsChange = (event: React.MouseEvent<HTMLElement>, value: string) => {
    updateFilter(FILTER_KEYS.BATHS.param, value);
  };

  const handleMoveInDateChange = (value: Date | null) => {
    const moveInDate = !value ? "" : value?.toISOString().split("T")[0];
    updateFilter(FILTER_KEYS.MOVE_IN_DATE.param, moveInDate);
  };

  const getMoveInDate = () => {
    if (!filters[FILTER_KEYS.MOVE_IN_DATE.param]) {
      return null;
    }
    return new Date(filters[FILTER_KEYS.MOVE_IN_DATE.param] as string);
  };

  const handleBudgetAriaLabel = (value: number) => {
    return `$${value}`;
  };

  const handleBudgetChange = (event: any, value: number | number[]) => {
    if (typeof value === "number") {
      updateFilter(FILTER_KEYS.BUDGET.param, `${value}`);
    }
  };

  const budgetValue = useMemo(() => {
    const budget = convertToNumber(filters[FILTER_KEYS.BUDGET.param]);
    const budgetValue = (budget ?? 0) > maxBudget || (budget ?? 0) === 0 ? maxBudget : (budget ?? maxBudget);
    return isNaN(budgetValue) ? maxBudget : budgetValue;
  }, [filters, FILTER_KEYS.BUDGET.param, maxBudget]);

  const bedOptions = useMemo(() => Array.from(new Set(floorplans.map((floorplan) => floorplan.bedrooms ?? 0))).sort((a, b) => a - b), [floorplans]);

  const BedroomsList = useMemo(
    () =>
      bedOptions.map((bed) => (
        <ToggleButton
          key={`${bed}_bedroom_toggle`}
          value={`${bed}`}
        >
          {AvailableHomesLogic.getBedroomsTranslation(bed, true)}
        </ToggleButton>
      )),
    [bedOptions]
  );

  const BathsList = useMemo(() => {
    const list = [];
    for (let i = minBath; i <= maxBath; i++) {
      list.push(
        <ToggleButton
          key={`${i}_bath_toggle`}
          value={`${i}`}
        >
          {i}
        </ToggleButton>
      );
    }
    return list;
  }, [minBath, maxBath]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className={cn(styles.container, isDialog && styles.dialog)}>
        {(availableHomesFilters.showBeds || isDialog) && (
          <div className={cn(styles.filterOption, styles.buttonGroup, isDialog && styles.dialog)}>
            <Typography>Beds</Typography>
            <ToggleButtonGroup
              id="toogle_group"
              color="primary"
              value={filters[FILTER_KEYS.BEDS.param]}
              onChange={handleBedsChange}
              aria-label="Platform"
              className={styles.toggleButtonGroup}
            >
              {BedroomsList}
            </ToggleButtonGroup>
          </div>
        )}
        {isDialog && (
          <div className={cn(styles.filterOption, styles.buttonGroup, isDialog && styles.dialog)}>
            <Typography>Baths</Typography>
            <ToggleButtonGroup
              id="toogle_group"
              color="primary"
              value={filters[FILTER_KEYS.BATHS.param]}
              onChange={handleBathsChange}
              aria-label="Platform"
              className={styles.toggleButtonGroup}
            >
              {BathsList}
            </ToggleButtonGroup>
          </div>
        )}
        {(availableHomesFilters.showMoveInDate || isDialog) && (
          <div className={cn(styles.filterOption, styles.datePicker, isDialog && styles.dialog)}>
            <Typography>Move-in Date</Typography>
            <DatePicker
              fullWidth={isDialog}
              name={"moveInDate"}
              views={["year", "month", "day"]}
              yearsPerRow={3}
              aria-label={"Move In Date"}
              value={getMoveInDate()}
              onChange={handleMoveInDateChange}
              sx={{
                width: isDialog ? "100%" : "auto",
                "& .MuiInputBase-root": {
                  width: isDialog ? "100%" : "auto",
                },
              }}
            />
          </div>
        )}
        {(availableHomesFilters.showBudget || isDialog) && (
          <div className={cn(styles.filterOption, styles.slider, isDialog && styles.dialog)}>
            <div className={cn(styles.sliderTitle, isDialog && styles.dialog)}>
              <Typography>Budget</Typography>
              <Typography>{`${currencySymbol}${StringHelper.addCommasToNumber(`${budgetValue}`)}`}</Typography>
            </div>
            <Slider
              aria-label="Temperature"
              defaultValue={maxBudget}
              getAriaValueText={handleBudgetAriaLabel}
              color="primary"
              value={budgetValue}
              min={minBudget}
              max={maxBudget}
              onChangeCommitted={handleBudgetChange}
            />
          </div>
        )}
        <div
          className={cn(styles.filterOption, styles.filterIcon, isDialog && styles.dialog)}
          onClick={openDialog}
        >
          <Image
            src="/image/icon_floorplans_filter.svg"
            alt="Filter"
            width={isSmall ? 32 : 50}
            height={isSmall ? 18 : 26}
          />
          {(filterCount ?? 0) > 0 && <div className={styles.filterCount}>{filterCount}</div>}
        </div>
      </div>
    </LocalizationProvider>
  );
}

export default AvailableHomesFiltersOptions;
